import React from 'react';
import clsx from 'clsx';
import {type PaletteColor} from '@mui/material/styles';
import {darken, lighten} from '@mui/system';
import {
	type ConfirmationOptions,
	useConfirmation,
} from '@netx/core-components/Confirmation';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	IconButton,
	type IconButtonProps,
} from '@netx/core-components/IconButton';
import {
	composeClasses,
	styled,
	type WithClasses,
	type WithOwnerState,
} from '@netx/core-components/Theme';
import {splitEventHandler} from '@netx/core-next/utils';
import {
	getIconButtonToggleUtilityClass,
	ICON_BUTTON_TOGGLE_CLASS_NAME,
	iconButtonToggleClasses,
	type IconButtonToggleClasses,
} from './iconButtonToggleClasses';

type IconButtonToggleUtiltiyClassProps = WithClasses<
	IconButtonToggleClasses,
	{
		on?: boolean;
		color: Required<IconButtonProps>['color'];
		fill?: boolean;
	}
>;

const useUtilityClasses = (ownerState: IconButtonToggleUtiltiyClassProps) => {
	const {classes, on, fill} = ownerState;
	const slots = {
		root: ['root', on && 'on', fill && 'fill'],
		background: ['background'],
	};
	return composeClasses(slots, getIconButtonToggleUtilityClass, classes);
};

const IconButtonToggleRoot = styled(IconButton, {
	name: ICON_BUTTON_TOGGLE_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}: WithOwnerState<IconButtonToggleUtiltiyClassProps>) => {
	const {color, fill} = ownerState || {};

	const light = theme.palette.mode === 'light';
	const getBackgroundColor = light ? darken : lighten;

	const palette = theme.palette[color as keyof typeof theme.palette] as
		| PaletteColor
		| undefined;
	const backgroundColor =
		color === 'inherit'
			? 'inherit'
			: getBackgroundColor(palette?.main ?? theme.palette.primary.main, 0.05);

	return {
		[`&.${iconButtonToggleClasses.on}`]: {
			color: palette?.contrastText ?? theme.palette.primary.contrastText,
			[`& .${iconButtonToggleClasses.background}`]: {
				transform: 'scale(1)',
			},
		},
		[`& .${iconButtonToggleClasses.background}`]: {
			backgroundColor,
			...(fill
				? {
						top: 0,
						right: 0,
						bottom: 0,
						left: 0,
				  }
				: {
						top: 4,
						right: 4,
						bottom: 4,
						left: 4,
				  }),
		},
	};
});

const IconButtonToggleBackground = styled('div', {
	name: ICON_BUTTON_TOGGLE_CLASS_NAME,
	slot: 'Background',
})(({theme}) => ({
	display: 'block',
	pointerEvents: 'none',
	borderRadius: '50%',
	transformOrigin: 'center',
	transform: 'scale(0)',
	transition: 'transform .15s ease-in-out',
	position: 'absolute',
}));

export interface IconButtonToggleProps
	extends Omit<
			IconButtonProps,
			'aria-pressed' | 'classes' | 'value' | 'variant'
		>,
		WithClasses<IconButtonToggleClasses> {
	/**
	 * Confirm toggling the button.
	 */
	confirm?: Omit<ConfirmationOptions, 'open'>;
	/**
	 * Remove the padding and make the background fill the entire button.
	 */
	fill?: boolean;
	/**
	 * Is the button toggled on.
	 */
	on?: boolean;
	onToggle: (
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		on: boolean,
	) => void;
}

export const IconButtonTogglePrimitive = ({
	classes: _classes,
	className,
	color = 'default',
	confirm,
	fill = false,
	on = false,
	onClick,
	onToggle,
	...props
}: IconButtonToggleProps) => {
	const confirmation = useConfirmation();

	const ownerState: IconButtonToggleUtiltiyClassProps = {
		classes: _classes,
		color,
		fill,
		on,
	};

	const classes = useUtilityClasses(ownerState);

	const handleClick = useConstCallback(
		splitEventHandler(onClick, (event) => {
			if (!confirm) return onToggle(event, !on);
			// we need to persist the event because the confirmation dialog is async
			event.persist();
			confirmation({
				message: confirm.message,
				title: confirm.title,
			}).then(() => onToggle(event, !on));
		}),
	);

	return (
		<IconButtonToggleRoot
			type="button"
			{...props}
			aria-pressed={on ? 'true' : 'false'}
			className={clsx(classes.root, className)}
			ownerState={ownerState}
			onClick={handleClick}
		>
			<IconButtonToggleBackground className={classes.background} />
		</IconButtonToggleRoot>
	);
};

export const IconButtonToggle = IconButtonTogglePrimitive;

export default IconButtonToggle;
